import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/use-current-user';
import { accountSettingsService, type FeatureMode } from '@/services/account-settings-service';
import {
  countApiKeysForProjects,
  revokeProximaApiKeysByOrganization,
} from '@/services/proxima-project-api-keys';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  RadioGroup,
  RadioGroupItem,
  toast,
} from 'prosci-ui';
import { Settings } from '@/lib/icons';

export function ProximaProjectSettingsTab() {
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const accountId = currentUser.organization_id;

  const [pendingProximaApiMode, setPendingProximaApiMode] = useState<FeatureMode | null>(null);

  const { data: accountSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['account-settings', accountId],
    queryFn: () => accountSettingsService.getSettings(accountId),
    enabled: !!accountId && !userLoading,
  });

  const { data: apiKeyCount } = useQuery({
    queryKey: ['proxima-api-key-count', accountId],
    queryFn: () => countApiKeysForProjects(accountId),
    enabled: !!accountId && !userLoading,
  });

  const proximaApiMode: FeatureMode = accountSettings?.proxima_api_mode ?? 'per_user';

  const setFeatureModeMutation = useMutation({
    mutationFn: async ({ featureCode, mode }: { featureCode: string; mode: FeatureMode }) => {
      await accountSettingsService.setFeatureMode(accountId, featureCode, mode);
      if (mode === 'disabled' && featureCode === 'proxima_api') {
        try {
          await revokeProximaApiKeysByOrganization(accountId);
        } catch (error) {
          console.error('Failed to revoke API keys:', error);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] });
      queryClient.invalidateQueries({ queryKey: ['accountSettings', accountId] });
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      queryClient.invalidateQueries({ queryKey: ['proxima-api-key-count', accountId] });
      queryClient.invalidateQueries({ queryKey: ['proxima-organization-api-keys', accountId] });
      toast.success('Feature setting updated successfully.');
    },
    onError: () => {
      toast.error('Failed to update feature setting. Please try again.');
    },
  });

  const handleProximaApiModeChange = (mode: FeatureMode) => {
    if (mode === 'disabled') {
      setPendingProximaApiMode('disabled');
      return;
    }
    setFeatureModeMutation.mutate({ featureCode: 'proxima_api', mode });
  };

  const handleConfirmProximaApiDisable = () => {
    setPendingProximaApiMode(null);
    setFeatureModeMutation.mutate({ featureCode: 'proxima_api', mode: 'disabled' });
  };

  if (userLoading || settingsLoading || !accountId) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Settings className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl">Proxima Settings</CardTitle>
              <CardDescription className="mt-1.5">
                Manage Proxima API access for your organization. Public Links are configured under Account Settings →
                App Level Settings.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y divide-gray-200">
            <div className="flex flex-col gap-2 py-4 first:pt-0 sm:flex-row sm:items-start sm:gap-4">
              <div className="flex items-start gap-3 sm:flex-1">
                <div className="mt-0.5 shrink-0 w-14" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <Label className="text-base font-medium">Proxima API</Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Allow users to generate API keys for Proxima projects that can export project data to
                    third-party tools.
                  </p>
                  <div className="mt-4 mb-2">
                    <span className="text-sm font-medium">Who can generate API keys:</span>
                  </div>
                  <RadioGroup
                    value={proximaApiMode}
                    onValueChange={(value: string) => handleProximaApiModeChange(value as FeatureMode)}
                    disabled={setFeatureModeMutation.isPending}
                    className="max-w-md"
                  >
                    <label
                      htmlFor="proxima-api-all-px"
                      className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                    >
                      <RadioGroupItem value="all_users" id="proxima-api-all-px" className="mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Enabled for All Users</span>
                        <p className="text-sm text-muted-foreground">
                          Enable API key generation for all Proxima users.
                        </p>
                      </div>
                    </label>
                    <label
                      htmlFor="proxima-api-disabled-px"
                      className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                    >
                      <RadioGroupItem value="disabled" id="proxima-api-disabled-px" className="mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Disabled for All Users</span>
                        <p className="text-sm text-muted-foreground">
                          Disable API key generation for all Proxima users.
                        </p>
                      </div>
                    </label>
                    <label
                      htmlFor="proxima-api-per-user-px"
                      className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                    >
                      <RadioGroupItem value="per_user" id="proxima-api-per-user-px" className="mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Configured per User</span>
                        <p className="text-sm text-muted-foreground">
                          Manage API key generation by user in Users → Edit Users → Apps → Proxima.
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!pendingProximaApiMode} onOpenChange={() => setPendingProximaApiMode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Proxima API</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Clicking Confirm will disable Proxima API access, revoking {apiKeyCount} project API keys,
                  and remove the ability of all users to generate Proxima project API keys. For more information:{' '}
                  <a
                    href="https://help.prosci.com/en/collections/18392245-license-admin-product-settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Learn more about License Admin Product Settings
                  </a>
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingProximaApiMode(null)}
              disabled={setFeatureModeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmProximaApiDisable}
              disabled={setFeatureModeMutation.isPending}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
